// ./utils/SortableTable.js
// src/components/Common/SortableTable.js

import React from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortUp, faSortDown } from '@fortawesome/free-solid-svg-icons';
import styles from './SortableTable.module.css';

const SortableTable = ({
  data,
  columns,
  isSelectable = false,
  selectedRows = {},
  onSelectRow = () => {},
  onSelectAll = () => {},
  enableStickyHeader = false,
  stickyFirstColumn = false,
  activeRowId = null,
  onRowClick = () => {},
  sorting, // <-- ADDED PROP for current sorting state
  onSortingChange, // <-- ADDED PROP for updating sorting state
}) => {
  const table = useReactTable({
    data,
    columns,
    state: { // <-- ADDED: Control sorting state externally
      sorting,
    },
    onSortingChange: onSortingChange, // <-- ADDED: Pass state updates to parent
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const isRowClickable = onRowClick.toString() !== (() => {}).toString();

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table}>
        {/* --- NEW: Add colgroup to define column widths --- */}
        <colgroup>
          {isSelectable && <col style={{ width: '50px' }} />} {/* Checkbox column */}
          {table.getHeaderGroups()[0].headers.map(header => (
            <col key={`col-${header.id}`} style={{ width: header.column.getSize() }}/>
          ))}
        </colgroup>
        <thead className={enableStickyHeader ? styles.stickyHeader : ''}>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {/* The "Select All" checkbox header cell */}
              {isSelectable && (
                <th className={stickyFirstColumn ? styles.stickyColumn : ''}>
                  <input
                    type="checkbox"
                    onChange={onSelectAll}
                    checked={data.length > 0 && Object.keys(selectedRows).length === data.length}
                  />
                </th>
              )}
              
              {/* Data header cells */}
              {headerGroup.headers.map((header, index) => (
                <th
                  key={header.id}
                  onClick={header.column.getToggleSortingHandler()}
                  // --- FIX ---
                  // Apply sticky class ONLY to the first data header (index 0)
                  className={`${header.column.getCanSort() ? styles.sortableHeader : ''} ${stickyFirstColumn && index === 0 ? styles.stickyColumn : ''}`}
                >
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <FontAwesomeIcon
                      icon={{ asc: faSortUp, desc: faSortDown }[header.column.getIsSorted()] ?? faSort}
                      className={styles.sortIcon}
                    />
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map(row => (
            <tr
              key={row.id}
              className={`${activeRowId === row.original.influencerId ? styles.activeRow : ''} ${isRowClickable ? styles.clickableRow : ''}`}
              onClick={() => onRowClick(row.original)}
            >
              {/* The individual row checkbox cell */}
              {isSelectable && (
                <td 
                  className={stickyFirstColumn ? styles.stickyColumn : ''}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    onChange={(e) => onSelectRow(e, row.original)}
                    checked={!!selectedRows[row.original.platformId]}
                  />
                </td>
              )}
              
              {/* Data cells */}
              {row.getVisibleCells().map((cell, index) => (
                <td
                  key={cell.id}
                  // --- FIX ---
                  // Apply sticky class ONLY to the first data cell (index 0)
                  className={stickyFirstColumn && index === 0 ? styles.stickyColumn : ''}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default SortableTable;